import {
  BulkUpsertRequest,
  BulkUpsertResponse,
  Contract,
  ContractReferenceData,
  ContractUpsertInput,
  DeleteContractsRequest,
  DeleteContractsResponse,
  FindContractsParams,
  PagedContractsResponse,
  StoredContractReferenceData,
  UpdateVerificationsResponse,
  UploadStructure,
  UploadSummaryResponse,
  UpsertContractResponse,
  VerificationRequest,
} from "../types/contract";
import httpService from "./httpService";
import { runWithFactorsRequestGuard } from "./factorsRequestLimiter";
import { FactorsResponse } from "./productFactorsService";

const FACTORS_PAGE_SIZE = 10000;
const MAX_CONTRACT_UPLOAD_CHUNK_BYTES = 30 * 1024 * 1024;
const MAX_CONTRACT_UPLOAD_BATCH_SIZE = 700;

export interface ContractUsageBasket {
  id: number;
  title: string;
  providerId: number;
}

export interface ContractUsagePortfolio {
  id: number;
  title: string;
  providerId: number;
}

export interface ContractUsageProvider {
  id: number;
  name: string;
}

export interface ContractUsageUser {
  id: string;
  name: string;
  email: string;
}

export interface ContractUsageResponse {
  baskets: ContractUsageBasket[];
  portfolios: ContractUsagePortfolio[];
  providers: ContractUsageProvider[];
  users: ContractUsageUser[];
}

export interface ContractPriceItem {
  datetimeUtc: string;
  conId: number;
  source: string;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
}

/**
 * Filters accepted by the streaming download. Same semantics as the list call; there are
 * deliberately no pagination or ordering parameters.
 */
export type DownloadContractsParams = Pick<
  FindContractsParams,
  "optionsAllowed" | "searchString" | "searchColumn" | "verifiedSinceMs"
>;

export interface ContractsDownloadResult {
  blob: Blob;
  /** Contracts received, counted from the newline-prefixed rows on the wire. */
  rowCount: number;
  byteCount: number;
}

export interface ContractPricesResponse {
  currentPage: number;
  itemsOnPage: number;
  totalPages: number;
  totalItems: number;
  items: ContractPriceItem[];
}

/**
 * Service for Contract V2 API operations
 * All endpoints use the /v2 prefix
 */
export class ContractsV2Service {
  private readonly baseUrl = "/v2/contracts";
  private readonly extendedTimeout = 300000; // 5 minutes for large contract operations

  constructor(
    private readonly maxUploadChunkBytes: number = MAX_CONTRACT_UPLOAD_CHUNK_BYTES
  ) {}

  /**
   * Find contracts with filtering, pagination, and ordering
   */
  async findContracts(params: FindContractsParams = {}): Promise<PagedContractsResponse> {
    const queryParams = new URLSearchParams();

    if (params.optionsAllowed !== undefined) {
      queryParams.set("optionsAllowed", String(params.optionsAllowed));
    }
    if (params.searchString) {
      queryParams.set("searchString", params.searchString);
    }
    if (params.searchColumn) {
      queryParams.set("searchColumn", params.searchColumn);
    }
    if (params.verifiedSinceMs !== undefined) {
      queryParams.set("verifiedSinceMs", String(params.verifiedSinceMs));
    }
    if (params.orderByColumn) {
      queryParams.set("orderByColumn", params.orderByColumn);
    }
    if (params.orderByDirection) {
      queryParams.set("orderByDirection", params.orderByDirection);
    }
    if (params.pageSize !== undefined) {
      queryParams.set("pageSize", String(params.pageSize));
    }
    if (params.pageNumber !== undefined) {
      queryParams.set("pageNumber", String(params.pageNumber));
    }
    if (params.structure) {
      queryParams.set("structure", params.structure);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const response = await httpService.get<PagedContractsResponse>(url, {
      timeout: this.extendedTimeout,
    });
    return response.data;
  }

  /**
   * Create or update a single contract
   */
  async upsertContract(contract: ContractUpsertInput): Promise<UpsertContractResponse> {
    const response = await httpService.post<UpsertContractResponse>(this.baseUrl, contract);
    return response.data;
  }

  /**
   * Read the stored reference data for a contract (admin only)
   */
  async getStoredReferenceData(conId: number): Promise<StoredContractReferenceData> {
    const response = await httpService.get<StoredContractReferenceData>(
      `${this.baseUrl}/${conId}/reference-data`
    );
    return response.data;
  }

  /**
   * Write stored reference data for a contract (admin only).
   *
   * The API merges rather than replaces: marketCap/volume are taken only when non-null
   * and non-zero, latestSplitDt/splitRatio only when non-null and non-empty. A value can
   * be corrected but never cleared here, so omitted keys mean "leave unchanged". The
   * write also stamps referenceDataReceivedAtUtc and recomputes avgDollarVolume.
   */
  async putStoredReferenceData(
    conId: number,
    referenceData: ContractReferenceData
  ): Promise<StoredContractReferenceData> {
    const response = await httpService.put<StoredContractReferenceData>(
      `${this.baseUrl}/${conId}/reference-data`,
      { referenceData }
    );
    return response.data;
  }

  /**
   * Delete contracts by conIds
   */
  async deleteContracts(conIds: number[]): Promise<DeleteContractsResponse> {
    const request: DeleteContractsRequest = { conIds };
    const response = await httpService.request<DeleteContractsResponse>({
      method: "DELETE",
      url: this.baseUrl,
      data: request,
    });
    return response.data;
  }

  /**
   * Get the expected file structure for CSV uploads
   */
  async getUploadStructure(): Promise<UploadStructure> {
    const response = await httpService.get<UploadStructure>(`${this.baseUrl}/upload-structure`);
    return response.data;
  }

  /**
   * Find contracts by an array of conIds
   */
  async findContractsByConIds(conIds: number[]): Promise<Contract[]> {
    const response = await httpService.post<{ contracts: Contract[] }>(
      `${this.baseUrl}/by-con-ids`,
      { conIds }
    );
    return response.data.contracts;
  }

  /**
   * Bulk upsert multiple contracts
   */
  async bulkUpsertContracts(contracts: ContractUpsertInput[]): Promise<BulkUpsertResponse> {
    const request: BulkUpsertRequest = { contracts };
    const response = await httpService.post<BulkUpsertResponse>(`${this.baseUrl}/bulk`, request);
    return response.data;
  }

  /**
   * Upload contracts from a CSV file
   */
  async uploadContractsFromFile(
    file: File,
    hasHeader: boolean = true,
    batchSize: number = MAX_CONTRACT_UPLOAD_BATCH_SIZE
  ): Promise<UploadSummaryResponse> {
    const chunks = await this.splitFileAtLineBoundaries(file);
    const combinedResult: UploadSummaryResponse = {
      success: true,
      summary: {
        totalParsed: 0,
        upsertedCount: 0,
        parseErrors: 0,
        failedContracts: [],
      },
    };

    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      try {
        const chunkResult = await this.uploadContractFileChunk(
          chunks[chunkIndex],
          hasHeader && chunkIndex === 0,
          batchSize
        );

        combinedResult.success = combinedResult.success && chunkResult.success;
        combinedResult.summary.totalParsed += chunkResult.summary.totalParsed;
        combinedResult.summary.upsertedCount += chunkResult.summary.upsertedCount;
        combinedResult.summary.parseErrors += chunkResult.summary.parseErrors;
        combinedResult.summary.failedContracts.push(...chunkResult.summary.failedContracts);
      } catch (error) {
        if (chunks.length === 1) throw error;
        const message = error instanceof Error ? error.message : "Unknown error";
        throw new Error(`Contract upload chunk ${chunkIndex + 1} of ${chunks.length} failed: ${message}`);
      }
    }

    return combinedResult;
  }

  private async splitFileAtLineBoundaries(file: File): Promise<File[]> {
    if (file.size <= this.maxUploadChunkBytes) return [file];

    const chunks: File[] = [];
    let chunkStart = 0;

    while (file.size - chunkStart > this.maxUploadChunkBytes) {
      const candidate = file.slice(chunkStart, chunkStart + this.maxUploadChunkBytes);
      const bytes = await this.readBlobBytes(candidate);
      let newlineIndex = bytes.length - 1;

      while (newlineIndex >= 0 && bytes[newlineIndex] !== 0x0a) {
        newlineIndex--;
      }

      if (newlineIndex < 0) {
        throw new Error("Contract upload contains a row larger than the request chunk limit");
      }

      const chunkEnd = chunkStart + newlineIndex + 1;
      chunks.push(this.createUploadChunk(file, chunkStart, chunkEnd));
      chunkStart = chunkEnd;
    }

    if (chunkStart < file.size) {
      chunks.push(this.createUploadChunk(file, chunkStart, file.size));
    }

    return chunks;
  }

  private readBlobBytes(blob: Blob): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
      reader.onerror = () => reject(reader.error ?? new Error("Failed to read contract upload"));
      reader.readAsArrayBuffer(blob);
    });
  }

  private createUploadChunk(file: File, start: number, end: number): File {
    return new File([file.slice(start, end)], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
  }

  private async uploadContractFileChunk(
    file: File,
    hasHeader: boolean,
    batchSize: number
  ): Promise<UploadSummaryResponse> {
    const formData = new FormData();
    formData.append("file", file);

    const queryParams = new URLSearchParams();
    queryParams.set("hasHeader", String(hasHeader));
    queryParams.set("batchSize", String(batchSize));

    const response = await httpService.post<UploadSummaryResponse>(
      `${this.baseUrl}/upload?${queryParams.toString()}`,
      formData,
      { timeout: this.extendedTimeout }
    );
    return response.data;
  }

  /**
   * Set verifiedAtUtc to current UTC time for the specified contracts
   */
  async setVerifications(conIds: number[]): Promise<UpdateVerificationsResponse> {
    const request: VerificationRequest = { conIds };
    const response = await httpService.post<UpdateVerificationsResponse>(
      `${this.baseUrl}/verifications/set`,
      request
    );
    return response.data;
  }

  /**
   * Clear verifiedAtUtc (set to null) for the specified contracts
   */
  async clearVerifications(conIds: number[]): Promise<UpdateVerificationsResponse> {
    const request: VerificationRequest = { conIds };
    const response = await httpService.post<UpdateVerificationsResponse>(
      `${this.baseUrl}/verifications/clear`,
      request
    );
    return response.data;
  }

  /**
   * Get paginated contract factors (date/factor pairs) for a given contract
   */
  async getContractFactorsPage(
    conId: number,
    pageNumber: number,
    from?: string,
    to?: string,
    pageSize: number = FACTORS_PAGE_SIZE
  ): Promise<FactorsResponse> {
    const params = new URLSearchParams();
    params.set("conId", String(conId));
    params.set("pageNumber", String(pageNumber));
    params.set("pageSize", String(pageSize));
    if (from) params.set("from", from);
    if (to) params.set("to", to);

    const response = await runWithFactorsRequestGuard(() =>
      httpService.get<FactorsResponse>(`${this.baseUrl}/factors?${params.toString()}`)
    );
    return response.data;
  }

  /**
   * Stream the whole filtered catalogue as flat delimited text.
   *
   * Replaces the old buffered `pageSize=150000&structure=flat` call, which built the entire
   * response server-side and failed in production. This endpoint streams and deliberately sends
   * no Content-Length, so there is no total to divide by -- progress is a count-up, never a
   * percentage. The response is a header row followed by one newline-prefixed row per contract,
   * so the newline count reported by the transport is exactly the number of contracts received.
   *
   * Ordering is fixed server-side (conId ascending) and there are no pagination parameters.
   */
  async downloadContractsStream(
    params: DownloadContractsParams = {},
    options: {
      signal?: AbortSignal;
      onProgress?: (progress: { rowCount: number; byteCount: number }) => void;
    } = {}
  ): Promise<ContractsDownloadResult> {
    const queryParams = new URLSearchParams();

    if (params.optionsAllowed !== undefined) {
      queryParams.set("optionsAllowed", String(params.optionsAllowed));
    }
    if (params.searchString) {
      queryParams.set("searchString", params.searchString);
    }
    if (params.searchColumn) {
      queryParams.set("searchColumn", params.searchColumn);
    }
    if (params.verifiedSinceMs !== undefined) {
      queryParams.set("verifiedSinceMs", String(params.verifiedSinceMs));
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `${this.baseUrl}/download?${queryString}`
      : `${this.baseUrl}/download`;

    return httpService.streamDownload(url, {
      headers: { Accept: "text/plain" },
      signal: options.signal,
      onProgress: options.onProgress,
    });
  }

  /**
   * Filename for a streamed download. Built client-side rather than parsed from the server's
   * Content-Disposition, which a cross-origin response only exposes if the header is listed in
   * Access-Control-Expose-Headers. The format matches what the server sends.
   */
  buildDownloadFileName(now: Date = new Date()): string {
    return `contracts-${now.toISOString().split("T")[0]}.txt`;
  }

  async getContractUsage(conId: number): Promise<ContractUsageResponse> {
    const response = await httpService.get<ContractUsageResponse>(
      `/v3/admin/contracts/usage?conId=${conId}`
    );
    return response.data;
  }

  async getContractPricesPage(
    conId: number,
    pageNumber: number,
    from?: string,
    to?: string
  ): Promise<ContractPricesResponse> {
    const params = new URLSearchParams();
    params.set("conId", String(conId));
    params.set("pageNumber", String(pageNumber));
    if (from) params.set("from", from);
    if (to) params.set("to", to);

    const response = await httpService.get<ContractPricesResponse>(
      `${this.baseUrl}/prices?${params.toString()}`
    );
    return response.data;
  }

  /**
   * Delete contract prices in plutus for a given contract over a UTC date range
   */
  async deleteContractPrices(conId: number, from: string, to: string): Promise<void> {
    const params = new URLSearchParams();
    params.set("conId", String(conId));
    params.set("from", from);
    params.set("to", to);
    await httpService.delete(`${this.baseUrl}/prices?${params.toString()}`);
  }

}

export const contractsV2Service = new ContractsV2Service();
export default contractsV2Service;
